import type { ReactElement } from 'react';
import { lazy, Suspense } from 'react';

import type { UserBannerDataResponse } from 'hb-builder/api/userBanner/types';
import SuspenseFallback from 'hb-builder/components/common/SuspenseFallback';
import { useGetProfileCompletion } from 'hb-builder/components/UIComponents/page/block/UserBanner/requests/useGetProfileCompletion';
import { getFormattedChildren } from 'hb-builder/components/UIComponents/page/block/UserBanner/utils/getFormattedChildren';
import type { DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';
import type { SectionItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/sectionItemAttributes';

const UserBannerScreenUI = {
  small: lazy(() => import('./UserBannerSmallScreenUI')),
  medium: lazy(() => import('./UserBannerMediumScreenUI')),
  large: lazy(() => import('./UserBannerLargeScreenUI')),
};

export enum SIZES {
  xs = 'small',
  s = 'small',
  m = 'medium',
  l = 'large',
  xl = 'large',
}

const NO_USER_LINKED_TO_ADMIN_MOCK_RATE = 50;

const UserBanner = ({
  ChildrenComponent,
  item,
  isBackOffice,
  size,
  user,
}: {
  ChildrenComponent: () => ReactElement;
  item?: DocumentItem<SectionItemAttributes>;
  isBackOffice: boolean;
  size: string;
  user?: UserBannerDataResponse;
}) => {
  const Component = UserBannerScreenUI[size];
  const { data: profileCompletionRateData, error } = useGetProfileCompletion();

  // if an admin is not linked to a user a 404 is expected, and we return a mock rate
  const shouldDisplayMockRate = isBackOffice && error?.response?.status === 404;

  const profileCompletionRate = shouldDisplayMockRate
    ? NO_USER_LINKED_TO_ADMIN_MOCK_RATE
    : Math.round(profileCompletionRateData || 0);

  const formattedChildren = getFormattedChildren({
    item,
    profileCompletionRate,
    user,
  });

  const {
    buttonAccount,
    buttonPost,
    buttonEvent,
    buttonPortfolio,
    headline,
    image,
    profileCompletion,
  } = formattedChildren;

  const props = {
    buttonAccount,
    buttonEvent,
    buttonPost,
    buttonPortfolio,
    dataTestId: 'user-banner-layout',
    headline,
    image,
    profileCompletion,
    profileCompletionRate,
    shouldShowCompletionRate: !profileCompletion?.attributes?.hidden,
  };

  return (
    <Suspense fallback={<SuspenseFallback />}>
      <Component ChildrenComponent={ChildrenComponent} {...props} />
    </Suspense>
  );
};

export default UserBanner;
